import AppLayout from '@/components/layout/AppLayout';
import PullToRefresh from '@/components/feed/PullToRefresh';
import PostCard from '@/components/feed/PostCard';
import BasketballLoader from '@/components/feed/BasketballLoader';
import RecipientPicker from '@/components/feed/RecipientPicker';
import { cn } from '@/lib/utils';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store/AppStoreProvider';
import { useSession } from '@/store/session';
import { usePreferences } from '@/store/preferences';
import { getUserById } from '@/store/users';
import type { Offer } from '@/store/offers';
import type { FeedPost } from '@/store/feed';
import { Compass, PenSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { notifyDmLimit } from '@/lib/dmLimit';

const Feed = () => {
  const { t } = usePreferences();
  const FILTERS = [t.feed_for_you, t.feed_highlights, t.feed_recruiting, t.feed_training, t.feed_announcement, t.feed_matches] as const;
  const FILTER_CATEGORIES: Record<string, string> = {
    [t.feed_highlights]: 'Highlights',
    [t.feed_recruiting]: 'Recruiting',
    [t.feed_training]: 'Training',
    [t.feed_announcement]: 'Announcement',
    [t.feed_matches]: 'Matches',
  };
  const [activeFilter, setActiveFilter] = useState<string>(t.feed_for_you);
  const [recipientPickerPost, setRecipientPickerPost] = useState<FeedPost | null>(null);
  
  const { currentUserId } = useSession();
  const { feed, follow, offer, message } = useAppStore();
  const navigate = useNavigate();
  const { posts, toggleLike, addComment, toggleCommentLike, toggleSave, repost, sharePost, isLiked, isSaved, editCaption, deletePost, archivePost, reportPost, deleteComment, pinComment, sortByRelevance, reportComment, canPostComment, recordCommentTime } = feed;

  const currentUser = currentUserId ? getUserById(currentUserId) : undefined;

  const visiblePosts = posts.filter((p) => !p.archived);

  const sorted = activeFilter === t.feed_for_you
    ? sortByRelevance(visiblePosts, currentUser?.role, offer.offers)
    : visiblePosts.filter((p) => p.category === FILTER_CATEGORIES[activeFilter]);

  // Infinite scroll: load the next feed page when the sentinel becomes visible.
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const loadMore = feed.loadMore;
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadMore();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [loadMore, feed.hasMore, feed.loading, sorted.length]);



  const handleSendInApp = (post: FeedPost) => {
    setRecipientPickerPost(post);
  };

  const handleSendToRecipients = async (userIds: string[], mode: 'individual' | 'group') => {
    if (!recipientPickerPost) return;

    const author = getUserById(recipientPickerPost.authorId);
    const sharedPost = {
      postId: recipientPickerPost.id,
      authorName: recipientPickerPost.authorName,
      authorInitials: recipientPickerPost.authorInitials,
      authorAvatarUrl: author?.avatarUrl,
      content: recipientPickerPost.content,
      imageUrl: recipientPickerPost.imageUrl,
      mediaItems: recipientPickerPost.mediaItems,
      postType: recipientPickerPost.postType,
    };

    let lastThreadId: string | null = null;

    if (mode === 'group' && userIds.length > 1) {
      const threadId = await message.getOrCreateGroupThread(userIds);
      if (!threadId) {
        notifyDmLimit(t, navigate);
        setRecipientPickerPost(null);
        return;
      }
      await message.sendMessage(threadId, '', sharedPost);
      lastThreadId = threadId;
    } else {
      for (const userId of userIds) {
        const threadId = await message.getOrCreateThread(userId);
        if (!threadId) {
          notifyDmLimit(t, navigate);
          setRecipientPickerPost(null);
          return;
        }
        await message.sendMessage(threadId, '', sharedPost);
        lastThreadId = threadId;
      }
    }


    setRecipientPickerPost(null);
    const label = mode === 'group'
      ? 'Post shared in group'
      : `Post shared with ${userIds.length} ${userIds.length === 1 ? 'person' : 'people'}`;
    toast.success(label);

    if (lastThreadId) {
      navigate(`/conversation/${lastThreadId}`);
    }
  };

  return (
    <AppLayout>
      <PullToRefresh eventName="feed-refresh" />


      {/* Filter bar */}
      <div className="flex gap-2 overflow-x-auto px-4 py-3 scrollbar-hide">
        {FILTERS.map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={cn(
              'shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200',
              activeFilter === filter
                ? 'bg-primary text-primary-foreground shadow-md shadow-primary/20'
                : 'bg-secondary text-muted-foreground hover:text-foreground'
            )}
          >
            {filter}
          </button>
        ))}
      </div>

      {/* Separator */}
      <div className="h-px bg-border/50" />

      {/* Loading / error states */}
      {feed.loading && (
        <div className="flex justify-center py-16">
          <BasketballLoader />
        </div>
      )}

      {!feed.loading && feed.error && (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
          <p className="text-sm text-muted-foreground">{feed.error}</p>
          <Button variant="outline" onClick={() => void feed.reload()} className="mt-4 rounded-full border-border">
            Retry
          </Button>
        </div>
      )}

      {/* Posts */}
      <div className="divide-y divide-border/30">
        {sorted.map((post) => {
          const authorId = post.originalPostId ? (post.originalAuthorId ?? post.authorId) : post.authorId;
          const linkedOffer: Offer | undefined = post.offerId ? offer.offers.find((o) => o.id === post.offerId) : undefined;
          const offerCreator = linkedOffer ? getUserById(linkedOffer.createdById) : undefined;
          return (
            <PostCard
              key={post.id}
              post={post}
              liked={isLiked(post.id)}
              saved={isSaved(post.id)}
              currentUserId={currentUserId!}
              currentUserRole={currentUser?.role}
              isFollowingAuthor={follow.isFollowing(authorId)}
              hasApplied={post.offerId ? offer.hasApplied(post.offerId) : false}
              applicantCount={post.offerId ? offer.getApplicationsForOffer(post.offerId).length : undefined}
              offer={linkedOffer}
              offerCreatorName={offerCreator?.name}
              onLike={() => toggleLike(post.id)}
              onComment={(text, parentId, mentions) => addComment(post.id, text, parentId, mentions)}
              onSave={() => toggleSave(post.id)}
              onRepost={() => repost(post.id)}
              onShare={() => sharePost(post.id)}
              onEditCaption={(text) => editCaption(post.id, text, post.taggedUserIds)}
              onDelete={() => deletePost(post.id, currentUserId!)}
              onArchive={() => archivePost(post.id)}
              onReport={(reason) => reportPost(post.id, reason)}
              onToggleFollowAuthor={() => follow.toggleFollow(authorId)}
              onApply={post.offerId ? () => offer.applyToOffer(post.offerId!) : undefined}
              onContactCreator={linkedOffer && linkedOffer.createdById !== currentUserId ? async () => {
                const threadId = await message.getOrCreateThread(linkedOffer.createdById, `Regarding: ${linkedOffer.title}`);
                if (!threadId) {
                  notifyDmLimit(t, navigate);
                  return;
                }
                navigate(`/conversation/${threadId}`);
              } : undefined}
              onSendInApp={() => handleSendInApp(post)}
              onDeleteComment={(commentId) => deleteComment(post.id, commentId)}
              onToggleCommentLike={(commentId) => toggleCommentLike(post.id, commentId)}
              onPinComment={(commentId) => pinComment(post.id, commentId)}
              onReportComment={(commentId, reason) => reportComment(post.id, commentId, reason)}
              canPostComment={canPostComment}
              onRecordCommentTime={recordCommentTime}
            />
          );
        })}
      </div>

      {/* Infinite scroll sentinel */}
      {!feed.loading && !feed.error && feed.hasMore && sorted.length > 0 && (
        <div ref={sentinelRef} className="flex justify-center py-8">
          {feed.loadingMore && <BasketballLoader />}
        </div>
      )}


      {/* Empty state */}
      {!feed.loading && !feed.error && sorted.length === 0 && (
        <div className="flex flex-col items-center justify-center px-6 py-16 text-center animate-fade-in">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Compass className="h-7 w-7 text-primary" />
          </div>
          <h3 className="text-lg font-bold text-foreground">
            {activeFilter === t.feed_for_you ? t.feed_empty_title : t.feed_no_posts.replace('{filter}', activeFilter)}
          </h3>
          <p className="mt-2 max-w-xs text-sm text-muted-foreground">
            {t.feed_empty_desc}
          </p>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => navigate('/create-post')} className="gap-2 rounded-full">
              <PenSquare className="h-4 w-4" /> {t.create_post}
            </Button>
            <Button variant="outline" onClick={() => navigate('/explore')} className="gap-2 rounded-full border-border">
              <Compass className="h-4 w-4" /> {t.explore}
            </Button>
          </div>
        </div>
      )}

      {/* Recipient picker for in-app sharing */}
      <RecipientPicker
        open={!!recipientPickerPost}
        onClose={() => setRecipientPickerPost(null)}
        currentUserId={currentUserId!}
        onSend={handleSendToRecipients}
      />
    </AppLayout>
  );
};

export default Feed;
